import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Coins, Edit, Plus, X } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import {
  formatRateHint,
  usdToVnd,
  useUsdVndRate,
  vndToUsd,
} from '../../services/exchangeRate.service';
import type { TokenPackCurrency } from '../../services/tokenPack.service';
import { FormattedNumberTextField } from '../common/FormattedNumberTextField';
import { PricingCalculatorPanel } from './pricing/PricingCalculatorPanel';
import { LocalizedTextFields } from './LocalizedTextFields';
import { useBreakEvenGuard } from './pricing/useBreakEvenGuard';

export interface TokenPackFormData {
  publicId?: string;
  code: string;
  name: string;
  descriptionEn: string;
  descriptionVi: string;
  tokenAmount: number;
  prices: Array<{
    currency: TokenPackCurrency;
    price: number;
    isActive: boolean;
  }>;
  isActive: boolean;
  sortOrder: number;
}

interface TokenPackFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: TokenPackFormData;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (data: TokenPackFormData) => void;
}

const getDefaultFormData = (): TokenPackFormData => ({
  code: '',
  name: '',
  descriptionEn: '',
  descriptionVi: '',
  tokenAmount: 1000000,
  prices: [
    { currency: 'VND', price: 199000, isActive: true },
    { currency: 'USD', price: 7.99, isActive: true },
  ],
  isActive: true,
  sortOrder: 100,
});

export const TokenPackFormDialog: React.FC<TokenPackFormDialogProps> = ({
  open,
  mode,
  initialData,
  isLoading = false,
  onClose,
  onSubmit,
}) => {
  const notification = useNotification();
  const [formData, setFormData] = useState<TokenPackFormData>(initialData || getDefaultFormData());
  const [breakEvenUsd, setBreakEvenUsd] = useState<number | null>(null);
  const breakEvenGuard = useBreakEvenGuard();

  useEffect(() => {
    if (!open) return;
    setFormData(initialData || getDefaultFormData());
  }, [initialData, open]);

  const handleFieldChange = <K extends keyof TokenPackFormData>(
    field: K,
    value: TokenPackFormData[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const { data: rate } = useUsdVndRate();

  // Editing one currency's price fills the other from the daily exchange rate; both stay editable.
  const handlePriceChange = (
    currency: TokenPackCurrency,
    field: 'price' | 'isActive',
    value: number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      prices: prev.prices.map((price) => {
        if (price.currency === currency) return { ...price, [field]: value };
        if (field !== 'price' || !rate || typeof value !== 'number') return price;
        return {
          ...price,
          price:
            currency === 'USD' ? usdToVnd(value, rate.vndPerUsd) : vndToUsd(value, rate.vndPerUsd),
        };
      }),
    }));
  };

  const handleSubmit = () => {
    if (!/^[A-Z0-9_]+$/.test(formData.code.trim())) {
      notification.error('Code must use uppercase letters, numbers, and underscores.');
      return;
    }

    if (!formData.name.trim()) {
      notification.error('Credit pack name is required.');
      return;
    }

    if (!Number.isInteger(formData.tokenAmount) || formData.tokenAmount <= 0) {
      notification.error('Credit amount must be a positive integer.');
      return;
    }

    if (formData.prices.length === 0 || !formData.prices.some((price) => price.isActive)) {
      notification.error('At least one active price is required.');
      return;
    }

    for (const price of formData.prices) {
      if (!Number.isFinite(price.price) || price.price < 0) {
        notification.error(`${price.currency} price must be non-negative.`);
        return;
      }
    }

    if (formData.prices.some((price) => price.isActive && price.price === 0)) {
      notification.error('Active prices must be greater than 0.');
      return;
    }

    if (!Number.isInteger(formData.sortOrder) || formData.sortOrder < 0) {
      notification.error('Sort order must be a non-negative integer.');
      return;
    }

    const usdPrice =
      formData.prices.find((price) => price.currency === 'USD' && price.isActive)?.price ?? 0;
    if (usdPrice > 0 && breakEvenUsd !== null && usdPrice < breakEvenUsd) {
      breakEvenGuard.requestSave(usdPrice, breakEvenUsd, () =>
        onSubmit({
          ...formData,
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
        })
      );
      return;
    }

    onSubmit({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-2">
            {mode === 'create' ? (
              <Plus size={24} className="text-amber-500" />
            ) : (
              <Edit size={24} className="text-amber-500" />
            )}
            <Typography variant="h6" className="font-semibold">
              {mode === 'create' ? 'Create Credit Pack' : 'Edit Credit Pack'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className="space-y-4 pt-2">
          <TextField
            label="Credit Pack Code"
            value={formData.code}
            onChange={(event) => handleFieldChange('code', event.target.value.toUpperCase())}
            placeholder="TOKEN_1M"
            fullWidth
            disabled={mode === 'edit'}
            helperText={
              mode === 'edit'
                ? 'Credit pack code cannot be changed'
                : 'Use uppercase letters, numbers, and underscores'
            }
            inputProps={{
              style: { fontFamily: 'monospace' },
            }}
          />

          <TextField
            label="Credit Pack Name"
            value={formData.name}
            onChange={(event) => handleFieldChange('name', event.target.value)}
            placeholder="1M Credits"
            fullWidth
            required
          />

          <Box className="space-y-2 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
            <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
              Shown on the user site under the token amount.
            </Typography>
            <LocalizedTextFields
              label="Description"
              valueEn={formData.descriptionEn}
              valueVi={formData.descriptionVi}
              onChange={(next) => {
                handleFieldChange('descriptionEn', next.en);
                handleFieldChange('descriptionVi', next.vi);
              }}
              multiline
              minRows={2}
              maxLength={1000}
              disabled={isLoading}
            />
          </Box>

          <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormattedNumberTextField
              label="Credit amount"
              value={formData.tokenAmount}
              onValueChange={(value) => handleFieldChange('tokenAmount', value ?? 0)}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Coins size={16} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 1,
                step: 1000,
              }}
            />

            <FormattedNumberTextField
              label="Sort order"
              value={formData.sortOrder}
              onValueChange={(value) => handleFieldChange('sortOrder', value ?? 0)}
              fullWidth
              required
              inputProps={{
                min: 0,
              }}
            />
          </Box>

          <PricingCalculatorPanel
            credits={formData.tokenAmount}
            priceUsd={formData.prices.find((price) => price.currency === 'USD')?.price ?? 0}
            kind="pack"
            onBreakEvenChange={setBreakEvenUsd}
            onUseSuggestedPrice={(value) => handlePriceChange('USD', 'price', value)}
          />
          <Box>
            <Typography
              variant="subtitle1"
              className="mb-2 font-semibold text-gray-700 dark:text-slate-300"
            >
              Prices by Currency
            </Typography>
            <Typography variant="caption" className="mb-2 block text-gray-500 dark:text-slate-400">
              {formatRateHint(rate)}. Editing one price auto-fills the other.
            </Typography>
            <Box className="space-y-3">
              {formData.prices.map((price) => (
                <Box
                  key={price.currency}
                  className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700 sm:grid-cols-[1fr_120px]"
                >
                  <FormattedNumberTextField
                    label={`${price.currency} price`}
                    value={price.price}
                    onValueChange={(value) =>
                      handlePriceChange(price.currency, 'price', value ?? 0)
                    }
                    integer={price.currency === 'VND'}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{price.currency}</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      min: 0,
                      step: price.currency === 'VND' ? 1000 : 0.01,
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={price.isActive}
                        onChange={(event) =>
                          handlePriceChange(price.currency, 'isActive', event.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
              ))}
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(event) => handleFieldChange('isActive', event.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={mode === 'create' ? <Plus size={18} /> : <Edit size={18} />}
          disabled={isLoading}
          sx={{
            backgroundColor: '#f59e0b',
            '&:hover': {
              backgroundColor: '#d97706',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {isLoading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
              ? 'Create Credit Pack'
              : 'Update Credit Pack'}
        </Button>
      </DialogActions>
      {breakEvenGuard.confirmDialog}
    </Dialog>
  );
};
